import { z, ZodError } from "zod";
import { Parseable } from "./types";

const maintenanceDataSchema = z.object({
  secondsLeft: z.number(),
  secondsLeftTimestampMs: z.number(),
});

export type MaintenanceData = z.infer<typeof maintenanceDataSchema>;

export const bridgeSchema = z.object({
  show: z.function(),
  close: z.function(),
  claimVoucher: z.function().args(z.string()),
  claimVoucherQuiet: z.function().args(z.string()),
  openUrl: z.function().args(z.string()),
  refreshJwt: z.function(),
  safeArea: z
    .object({
      left: z.number(),
      right: z.number(),
    })
    .optional(),
  getParams: z
    .function()
    .returns(z.promise(z.string()))
    .optional()
    .transform((getParams) => {
      return <T>(
        parser: Parseable<T>,
      ): ReturnType<typeof parseBridgeFunctionResult<T>> & {
        bridgeShim?: boolean;
      } => parseBridgeFunctionResult(getParams, parser);
    }),
  markTabAsSeen: z.function().args(z.string()),
  playSound: z.function().args(z.string()),
  goBack: z.function(),
  openShop: z.function(),
  openUrlWithSso: z.function().args(z.string()),
  scidOpenWebSSO: z.function().args(z.string()).optional(),
  maintenance: maintenanceDataSchema.optional(),
});

export const windowSchema = z.object({
  bridge: bridgeSchema,
});

function parseResponseData<T>(
  input: unknown,
  parser: Parseable<T>,
): { output: T; validationError?: ZodError } {
  try {
    return { output: parser.parse(input) };
  } catch (ex) {
    if (ex instanceof ZodError) {
      return { output: input as T, validationError: ex };
    }

    throw ex;
  }
}

export const parseBridgeFunctionResult = async <T>(
  bridgeFn: ((...args: unknown[]) => Promise<string>) | undefined,
  parser: Parseable<T>,
) => {
  const getParamsRes = await bridgeFn?.();

  const { output: gameParams, validationError } = parseResponseData<T>(
    JSON.parse(getParamsRes || '{ "data": {} }').data,
    parser,
  );

  return { gameParams, validationError };
};

export type Bridge = z.infer<typeof bridgeSchema>;

export type BridgeFunctionName = keyof Bridge;
